/**
 * Per-field provenance for a bill built from an e-mail attachment (ALC-4469, decisions Q4/Q8/Q12/Q12b).
 *
 * The document wins. The e-mail body only fills a field the document left empty; when both carry
 * a value and they differ, the document value is kept and the field is a CONFLICT. The decision is
 * recorded at the moment the body is merged into the extraction (never reconstructed by diffing
 * later), stored on the source document metadata under {@link EMAIL_BODY_FIELD_PROVENANCE_METADATA_KEY}
 * and copied onto `ApBill.intakeFieldProvenance` when the bill is created.
 *
 * Lives in `@alinea/types` so the web bill page (S4) can read the same shape and copy.
 */

import { ApPaymentStatus } from '../generated/enums.js';
import type { StableRefusalCopy } from '../stable-refusal.js';

export const EMAIL_BODY_FIELD_PROVENANCE_VERSION = 1 as const;

/** Key on `ApDocument.metadata` where the merge step stores its provenance. */
export const EMAIL_BODY_FIELD_PROVENANCE_METADATA_KEY = 'emailBodyFieldProvenance' as const;

export const EmailBodyFieldSource = {
  /** The document carried the value; the body had none or the same value. */
  DOCUMENT: 'DOCUMENT',
  /** The document had no value; the e-mail body filled it ("do e-mail"). */
  EMAIL: 'EMAIL',
  /** Both had a value and they differ; the document value was kept ("conferir"). */
  CONFLICT: 'CONFLICT',
} as const;
export type EmailBodyFieldSource = (typeof EmailBodyFieldSource)[keyof typeof EmailBodyFieldSource];

/** Bill fields the body can fill. */
export const EmailBodyField = {
  PIX_KEY: 'pixKey',
  PIX_COPIA_E_COLA: 'pixCopiaECola',
  DUE_DATE: 'dueDate',
  ISSUE_DATE: 'issueDate',
  TOTAL_AMOUNT: 'totalAmount',
  VENDOR_TAX_ID: 'vendorTaxId',
  VENDOR_NAME: 'vendorName',
  DOCUMENT_NUMBER: 'documentNumber',
} as const;
export type EmailBodyField = (typeof EmailBodyField)[keyof typeof EmailBodyField];

/** Why a field asks the approver to check it ("conferir"). Stable codes. */
export const EmailBodyFieldReviewReason = {
  /** Document and body disagree; the document value was kept (Q4). */
  CONFLICT_WITH_DOCUMENT: 'CONFLICT_WITH_DOCUMENT',
  /** Amount, vendor or CNPJ came from free text (Q12b). */
  SENSITIVE_FIELD_FROM_EMAIL: 'SENSITIVE_FIELD_FROM_EMAIL',
  /** A payment destination (PIX key / copia-e-cola) came from free text (ledger: payment redirection). */
  PAYMENT_DESTINATION_FROM_EMAIL: 'PAYMENT_DESTINATION_FROM_EMAIL',
  /** The issue date came from free text: it drives competência and the FX date (D6). */
  ISSUE_DATE_FROM_EMAIL: 'ISSUE_DATE_FROM_EMAIL',
  /**
   * The body names an amount the merge did not apply: another currency, a document with no
   * currency, or a document with withholdings (D7/D9). The document amount stays empty.
   */
  AMOUNT_NOT_APPLIED_FROM_EMAIL: 'AMOUNT_NOT_APPLIED_FROM_EMAIL',
} as const;
export type EmailBodyFieldReviewReason =
  (typeof EmailBodyFieldReviewReason)[keyof typeof EmailBodyFieldReviewReason];

export interface EmailBodyFieldProvenanceEntry {
  source: EmailBodyFieldSource;
  /**
   * The value the bill carried when the merge ran (document value for DOCUMENT/CONFLICT, body
   * value for EMAIL). A later "Usar o do e-mail" changes the bill, not this field: the decision is
   * in `resolution`.
   */
  value: string | number | null;
  /** The value the body proposed (present for EMAIL and CONFLICT). */
  emailValue?: string | number | null;
  /** The value the document carried (present for DOCUMENT and CONFLICT). */
  documentValue?: string | number | null;
  /** The body sentence the value was read from, when it could be located (max 240 chars). */
  snippet?: string | null;
  /** True when this field asks the approver to check it ("conferir"). */
  needsReview: boolean;
  reviewReason?: EmailBodyFieldReviewReason;
  /**
   * Added by the bill page (ALC-4470) when a person decided this field. `source` and
   * `reviewReason` are never rewritten: the tag still says where the value came from, and payment
   * creation keys on `source`. Only `needsReview` flips to false.
   */
  resolution?: EmailBodyFieldResolution;
}

/** What the person at the bill page did with a field that asked for "conferir" (ALC-4470). */
export const IntakeProvenanceDecision = {
  /** A CONFLICT: the bill now carries the e-mail value ("Usar o do e-mail"). */
  USE_EMAIL: 'USE_EMAIL',
  /** A CONFLICT: the bill keeps the document value ("Manter o da nota"). */
  KEEP_DOCUMENT: 'KEEP_DOCUMENT',
  /** An EMAIL field: the person checked the value the e-mail filled ("Está certo"). */
  CONFIRM: 'CONFIRM',
} as const;
export type IntakeProvenanceDecision =
  (typeof IntakeProvenanceDecision)[keyof typeof IntakeProvenanceDecision];

export interface EmailBodyFieldResolution {
  decision: IntakeProvenanceDecision;
  resolvedByUserId: string;
  /** ISO timestamp. */
  resolvedAt: string;
}

/** Why the merge ran but applied nothing from the body. Stable codes. */
export const EmailBodyMergeSkipReason = {
  /** The e-mail carries two or more payable attachments: its body cannot be tied to one (D4). */
  MULTIPLE_PAYABLE_ATTACHMENTS: 'MULTIPLE_PAYABLE_ATTACHMENTS',
  /** A statutory guia (DARF, GPS, GUIA_*, DAS, GRF): the government document is the only source (D11). */
  STATUTORY_GUIA: 'STATUTORY_GUIA',
} as const;
export type EmailBodyMergeSkipReason =
  (typeof EmailBodyMergeSkipReason)[keyof typeof EmailBodyMergeSkipReason];

export interface EmailBodyFieldProvenance {
  version: typeof EMAIL_BODY_FIELD_PROVENANCE_VERSION;
  /**
   * Set when the merge ran for an e-mail document but applied nothing from the body. `fields` is
   * then empty. Its presence (rather than no provenance at all) keeps the payment on the document:
   * no provenance means "extracted before the merge existed" and lets the e-mail drive the payment.
   */
  skipped?: { reason: EmailBodyMergeSkipReason };
  /** The EmailMessage the body came from, when known. */
  sourceMessageId: string | null;
  /** The ApDocument whose extraction the body was merged into. */
  documentId: string | null;
  mergedAt: string;
  /** True when any field asks for "conferir". */
  needsReview: boolean;
  fields: Partial<Record<EmailBodyField, EmailBodyFieldProvenanceEntry>>;
}

/**
 * `ApBillAlert.metadata.source` of the "conferir" alert. The auto-submit guard blocks on it, the
 * same way it blocks on `INTAKE_RULE`.
 */
export const EMAIL_BODY_REVIEW_ALERT_SOURCE = 'EMAIL_BODY' as const;

export const EmailBodyFieldAlertCode = {
  EMAIL_BODY_FIELDS_NEED_REVIEW: 'EMAIL_BODY_FIELDS_NEED_REVIEW',
} as const;
export type EmailBodyFieldAlertCode =
  (typeof EmailBodyFieldAlertCode)[keyof typeof EmailBodyFieldAlertCode];

export const EMAIL_BODY_FIELD_ALERT_MESSAGES: Record<
  EmailBodyFieldAlertCode,
  { ptBr: string; en: string }
> = {
  EMAIL_BODY_FIELDS_NEED_REVIEW: {
    ptBr: 'Conferir: um ou mais campos vieram do texto do e-mail ou divergem do documento. Confira antes de enviar para aprovação.',
    en: 'Check: one or more fields came from the e-mail text or disagree with the document. Check them before sending for approval.',
  },
};

export const EMAIL_BODY_FIELD_LABELS: Record<EmailBodyField, { ptBr: string; en: string }> = {
  pixKey: { ptBr: 'Chave PIX', en: 'PIX key' },
  pixCopiaECola: { ptBr: 'PIX copia e cola', en: 'PIX copy-and-paste code' },
  dueDate: { ptBr: 'Vencimento', en: 'Due date' },
  issueDate: { ptBr: 'Data de emissão', en: 'Issue date' },
  totalAmount: { ptBr: 'Valor', en: 'Amount' },
  vendorTaxId: { ptBr: 'CNPJ/CPF do fornecedor', en: 'Vendor tax ID' },
  vendorName: { ptBr: 'Fornecedor', en: 'Vendor' },
  documentNumber: { ptBr: 'Número do documento', en: 'Document number' },
};

export const EMAIL_BODY_FIELD_REVIEW_REASON_MESSAGES: Record<
  EmailBodyFieldReviewReason,
  { ptBr: string; en: string }
> = {
  CONFLICT_WITH_DOCUMENT: {
    ptBr: 'O e-mail diz outro valor; mantivemos o do documento.',
    en: 'The e-mail says something else; the document value was kept.',
  },
  SENSITIVE_FIELD_FROM_EMAIL: {
    ptBr: 'Valor, fornecedor ou CNPJ veio do texto do e-mail.',
    en: 'Amount, vendor or tax ID came from the e-mail text.',
  },
  PAYMENT_DESTINATION_FROM_EMAIL: {
    ptBr: 'O destino do pagamento veio do texto do e-mail.',
    en: 'The payment destination came from the e-mail text.',
  },
  ISSUE_DATE_FROM_EMAIL: {
    ptBr: 'A data de emissão veio do texto do e-mail. Ela define a competência e a data do câmbio.',
    en: 'The issue date came from the e-mail text. It sets the accrual period and the FX date.',
  },
  AMOUNT_NOT_APPLIED_FROM_EMAIL: {
    ptBr: 'O e-mail informa um valor que não aplicamos (outra moeda, documento sem moeda ou com retenções). Confira o valor.',
    en: 'The e-mail names an amount we did not apply (another currency, a document with no currency or with withholdings). Check the amount.',
  },
};

export const EMAIL_BODY_MERGE_SKIP_REASON_MESSAGES: Record<
  EmailBodyMergeSkipReason,
  { ptBr: string; en: string }
> = {
  MULTIPLE_PAYABLE_ATTACHMENTS: {
    ptBr: 'O e-mail tem mais de um documento a pagar; o texto do e-mail não foi usado.',
    en: 'The e-mail has more than one payable document; the e-mail text was not used.',
  },
  STATUTORY_GUIA: {
    ptBr: 'Guia de tributo: só os dados da guia valem; o texto do e-mail não foi usado.',
    en: 'Tax guide: only the guide itself counts; the e-mail text was not used.',
  },
};

/** Reads a stored provenance object defensively (bill column or document metadata). */
export function readEmailBodyFieldProvenance(value: unknown): EmailBodyFieldProvenance | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (record.version !== EMAIL_BODY_FIELD_PROVENANCE_VERSION) return null;
  if (!record.fields || typeof record.fields !== 'object' || Array.isArray(record.fields)) {
    return null;
  }
  return value as EmailBodyFieldProvenance;
}

/**
 * The comparable form of a document number: letters and digits only, no leading zeros, upper case.
 * The merge compares with it ("0087" and "87" are one number), and the resolve action uses the same
 * rule to check the bill holds the e-mail's number.
 */
export function normalizeEmailBodyDocumentNumber(value: string): string {
  return value
    .replace(/[^0-9a-z]/gi, '')
    .replace(/^0+/, '')
    .toUpperCase();
}

// ─── Resolving a field on the bill page (ALC-4470) ───────────────────────────

/**
 * Every withholding amount column of `ApBill` (BR retentions, MX IVA retenido and the total). A
 * non-zero value in any of them means the bill's gross is not the whole story: net payable and the
 * retained tax are derived from it, so the e-mail's amount must never replace it. The API test
 * walks the Prisma model and fails when a `*WithholdingAmount` column is missing here.
 */
export const INTAKE_BILL_WITHHOLDING_COLUMNS = [
  'irrfWithholdingAmount',
  'issWithholdingAmount',
  'ivaWithholdingAmount',
  'pisWithholdingAmount',
  'cofinsWithholdingAmount',
  'csllWithholdingAmount',
  'inssWithholdingAmount',
  'totalWithholdingAmount',
] as const;
export type IntakeBillWithholdingColumn = (typeof INTAKE_BILL_WITHHOLDING_COLUMNS)[number];

/**
 * The bill columns the provenance rules read. Amounts arrive as a number (web), a decimal string
 * or a Prisma `Decimal` (API); anything that stringifies to a number works.
 */
export type IntakeProvenanceBillColumns = Partial<Record<IntakeBillWithholdingColumn, unknown>> & {
  currencyCode?: string | null;
  originalCurrencyCode?: string | null;
};

const isNonZeroAmount = (amount: unknown): boolean => {
  if (amount == null || amount === '') return false;
  const numeric = Number(String(amount));
  // An unreadable value is treated as a withholding: failing closed keeps the e-mail amount out.
  return Number.isNaN(numeric) || numeric !== 0;
};

/** True when ANY withholding column of the bill is non-zero. One rule for the API and the web. */
export function billHasAnyWithholding(bill: IntakeProvenanceBillColumns): boolean {
  return INTAKE_BILL_WITHHOLDING_COLUMNS.some((column) => isNonZeroAmount(bill[column]));
}

/**
 * What the bill says about itself that bears on taking the e-mail's amount. The provenance stores
 * neither the currency nor gross/net, so the eligibility rule asks the bill.
 */
export interface IntakeProvenanceBillContext {
  /** The bill carries a foreign original currency (its header was converted). */
  hasForeignCurrency: boolean;
  /** Any withholding on the bill is non-zero ({@link billHasAnyWithholding}). */
  hasWithholdings: boolean;
}

/** The eligibility context of a bill, derived the same way on the API and on the web. */
export function intakeProvenanceBillContextOf(
  bill: IntakeProvenanceBillColumns,
): IntakeProvenanceBillContext {
  return {
    hasForeignCurrency:
      bill.originalCurrencyCode != null && bill.originalCurrencyCode !== bill.currencyCode,
    hasWithholdings: billHasAnyWithholding(bill),
  };
}

/**
 * Payments that hold the bill's current amount. "Usar o do e-mail" on the amount is refused while
 * one exists: the bill would change and the payment would not. SCHEDULED, PENDING and PROCESSING
 * can still leave the account at the old amount; FAILED can be retried at it; PAID and RECONCILED
 * already moved money at it, so a new bill amount would leave the books and the bank apart (fix
 * round 2). Only CANCELLED and REVERSED are void and do not block.
 */
export const INTAKE_PROVENANCE_BLOCKING_PAYMENT_STATUSES = [
  ApPaymentStatus.SCHEDULED,
  ApPaymentStatus.PENDING,
  ApPaymentStatus.PROCESSING,
  ApPaymentStatus.FAILED,
  ApPaymentStatus.PAID,
  ApPaymentStatus.RECONCILED,
] as const;

/**
 * Fields where "Usar o do e-mail" is a plain edit of one bill column through the existing update
 * endpoint. A payment destination or the vendor identity is never replaced by e-mail text: they
 * get "Manter o da nota" only.
 */
const USE_EMAIL_FIELDS: ReadonlySet<EmailBodyField> = new Set<EmailBodyField>([
  EmailBodyField.DUE_DATE,
  EmailBodyField.ISSUE_DATE,
  EmailBodyField.DOCUMENT_NUMBER,
  EmailBodyField.TOTAL_AMOUNT,
]);

/** Whether the person may replace the bill's value with the e-mail's, for a CONFLICT entry. */
export function canUseEmailValueForField(
  field: EmailBodyField,
  entry: EmailBodyFieldProvenanceEntry,
  context?: IntakeProvenanceBillContext | null,
): boolean {
  if (entry.source !== EmailBodyFieldSource.CONFLICT) return false;
  if (!USE_EMAIL_FIELDS.has(field)) return false;
  if (entry.emailValue == null) return false;
  if (field === EmailBodyField.TOTAL_AMOUNT) {
    // AMOUNT_NOT_APPLIED_FROM_EMAIL names an amount in another currency, with no currency, or on a
    // document with withholdings: replacing the gross would put a number in the wrong frame.
    if (entry.reviewReason !== EmailBodyFieldReviewReason.CONFLICT_WITH_DOCUMENT) return false;
    if (!context || context.hasForeignCurrency || context.hasWithholdings) return false;
  }
  return true;
}

/** The decisions the person can take on one field right now. Empty when nothing is pending. */
export function allowedIntakeProvenanceDecisions(
  field: EmailBodyField,
  entry: EmailBodyFieldProvenanceEntry,
  context?: IntakeProvenanceBillContext | null,
): IntakeProvenanceDecision[] {
  if (!entry.needsReview) return [];
  if (entry.source === EmailBodyFieldSource.EMAIL) return [IntakeProvenanceDecision.CONFIRM];
  if (entry.source === EmailBodyFieldSource.CONFLICT) {
    return canUseEmailValueForField(field, entry, context)
      ? [IntakeProvenanceDecision.USE_EMAIL, IntakeProvenanceDecision.KEEP_DOCUMENT]
      : [IntakeProvenanceDecision.KEEP_DOCUMENT];
  }
  return [];
}

export interface ResolveIntakeProvenanceRequest {
  field: EmailBodyField;
  decision: IntakeProvenanceDecision;
}

export interface ResolveIntakeProvenanceResponse {
  billId: string;
  /** The provenance after the decision, `needsReview` recomputed. */
  intakeFieldProvenance: EmailBodyFieldProvenance;
  /** True when this decision left no field to check and the EMAIL_BODY alert was resolved. */
  alertResolved: boolean;
}

/** Stable refusal codes of the resolve action. Classify by these, never by message text. */
export const IntakeProvenanceErrorCode = {
  BILL_NOT_FOUND: 'INTAKE_PROVENANCE_BILL_NOT_FOUND',
  BILL_NOT_EDITABLE: 'INTAKE_PROVENANCE_BILL_NOT_EDITABLE',
  NOT_AVAILABLE: 'INTAKE_PROVENANCE_NOT_AVAILABLE',
  FIELD_NOT_FOUND: 'INTAKE_PROVENANCE_FIELD_NOT_FOUND',
  FIELD_NOT_PENDING: 'INTAKE_PROVENANCE_FIELD_NOT_PENDING',
  DECISION_NOT_ALLOWED: 'INTAKE_PROVENANCE_DECISION_NOT_ALLOWED',
  USE_EMAIL_UNSUPPORTED: 'INTAKE_PROVENANCE_USE_EMAIL_UNSUPPORTED',
  EMAIL_VALUE_NOT_APPLIED: 'INTAKE_PROVENANCE_EMAIL_VALUE_NOT_APPLIED',
  /** "Usar o do e-mail" on the amount while a payment holds the old amount (settled ones too). */
  PAYMENT_SCHEDULED: 'INTAKE_PROVENANCE_PAYMENT_SCHEDULED',
  /** "Manter" / "Está certo" on a field whose bill value changed since the e-mail was read. */
  DOCUMENT_VALUE_CHANGED: 'INTAKE_PROVENANCE_DOCUMENT_VALUE_CHANGED',
} as const;
export type IntakeProvenanceErrorCode =
  (typeof IntakeProvenanceErrorCode)[keyof typeof IntakeProvenanceErrorCode];

export const INTAKE_PROVENANCE_ERROR_MESSAGES: Record<
  IntakeProvenanceErrorCode,
  StableRefusalCopy
> = {
  INTAKE_PROVENANCE_BILL_NOT_FOUND: {
    ptBr: 'Não encontramos esta conta.',
    en: 'We could not find this bill.',
    hint: 'Check the billId; the bill may be deleted or belong to an entity you cannot access.',
  },
  INTAKE_PROVENANCE_BILL_NOT_EDITABLE: {
    ptBr: 'Esta conta já não pode ser alterada. Reabra-a para conferir os campos.',
    en: 'This bill can no longer be changed. Reopen it to check the fields.',
    hint: 'Reopen the bill (back to draft) before resolving its e-mail fields.',
  },
  INTAKE_PROVENANCE_NOT_AVAILABLE: {
    ptBr: 'Esta conta não tem dados do e-mail para conferir.',
    en: 'This bill has no e-mail data to check.',
    hint: 'This bill has no intakeFieldProvenance; there is nothing to resolve.',
  },
  INTAKE_PROVENANCE_FIELD_NOT_FOUND: {
    ptBr: 'O texto do e-mail não preencheu nem contestou este campo.',
    en: 'The e-mail text did not fill or dispute this field.',
    hint: 'Use a field listed in the bill intakeFieldProvenance.',
  },
  INTAKE_PROVENANCE_FIELD_NOT_PENDING: {
    ptBr: 'Este campo já foi conferido.',
    en: 'This field has already been checked.',
    hint: 'Reload the bill provenance; this field needs no decision.',
  },
  INTAKE_PROVENANCE_DECISION_NOT_ALLOWED: {
    ptBr: 'Esta ação não se aplica a este campo.',
    en: 'This action does not apply to this field.',
    hint: 'Use one of the decisions allowed for this field (allowedIntakeProvenanceDecisions).',
  },
  INTAKE_PROVENANCE_USE_EMAIL_UNSUPPORTED: {
    ptBr: 'Este campo não pode ser trocado pelo valor do e-mail. Mantenha o da nota ou altere a conta pela edição.',
    en: 'This field cannot be replaced by the e-mail value. Keep the document value or change the bill by editing it.',
    hint: 'Use KEEP_DOCUMENT, or edit the bill field through the bill update endpoint.',
  },
  INTAKE_PROVENANCE_EMAIL_VALUE_NOT_APPLIED: {
    ptBr: 'A conta ainda não tem o valor do e-mail. Atualize a página e tente de novo.',
    en: 'The bill does not hold the e-mail value yet. Refresh the page and try again.',
    hint: 'Update the bill field to the e-mail value first, then resolve with USE_EMAIL.',
  },
  INTAKE_PROVENANCE_PAYMENT_SCHEDULED: {
    ptBr: 'Há um pagamento nesta conta (agendado ou já feito). Ajuste o pagamento antes de trocar o valor.',
    en: 'This bill has a payment (scheduled or already made). Adjust the payment before changing the amount.',
    hint: 'Cancel or adjust the payment that holds the old amount, then retry.',
  },
  INTAKE_PROVENANCE_DOCUMENT_VALUE_CHANGED: {
    ptBr: 'O valor deste campo mudou na conta depois da leitura do e-mail. Atualize a página e confira de novo.',
    en: 'This field changed on the bill after the e-mail was read. Refresh the page and check it again.',
    hint: 'Reload the bill; its value changed after the e-mail was read. Decide again on the current value.',
  },
};
